import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import axios from 'axios'
import { fetchFeedsV2Market, streamFeedsV2Market, setFeedsV2FixturePricing, setFeedsV2EventMapping, ensureFeedsV2Session } from '../services/feedsV2Api.js'

// One fixture stream feeds all cell badges; cells never open their own connections.
export const useFeedsV2Store = defineStore('feedsV2', () => {
  const fixtureId = ref(null), snapshot = ref(null), error = ref(''), busy = ref(false), connection = ref('offline'), clock = ref(Date.now())
  let generation = 0, abort, stream, ticker, renewal, routingTimer
  // Laravel side: are feed prices saved to the lines, and who gets fixture.updated after a change.
  const routing = ref(null)
  async function loadRouting(id) {
    try {
      const { data } = await axios.get(`/api/feeds-v2/fixtures/${id}/status`)
      if (fixtureId.value === Number(id)) routing.value = data
    } catch { if (fixtureId.value === Number(id)) routing.value = null }
  }
  const routingLabel = computed(() => {
    const r = routing.value
    if (!r) return ''
    if (!r.line_writes_enabled) return 'Preview only · not saved'
    const plan = r.send_plan || []
    if (plan.includes('*')) return 'Saved · sent to all clients'
    if (!plan.length) return 'Saved · not sent'
    return r.published ? `Saved · sent to ${plan.join(', ')}` : 'Saved · test endpoint only (unpublished)'
  })
  const stale = computed(() => !snapshot.value || clock.value - snapshot.value.generatedAt > 15000 || snapshot.value.engine?.workerStale || snapshot.value.engine?.status !== 'healthy')
  const fixtureMode = computed(() => snapshot.value?.fixturePricingModes?.[fixtureId.value]?.enabled === true)
  const canEdit = computed(() => snapshot.value?.pricingAccess?.canEdit === true && (snapshot.value?.pricingAccess?.allFixtures === true || snapshot.value?.pricingAccess?.fixtureIds?.includes(fixtureId.value)))
  const canEnable = computed(() => canEdit.value && !stale.value)
  // Trader-confirmed OddsMarket games for this fixture; a new switch-on needs one when required.
  const eventMapping = computed(() => snapshot.value?.eventMappings?.[fixtureId.value] || null)
  const requireEventMapping = computed(() => snapshot.value?.pricingAccess?.requireEventMapping === true)
  const eligibleCount = computed(() => stale.value ? 0 : (snapshot.value?.markets || []).filter(m => m.decision === 'ready' && !m.stale && !m.sourceMissing).length)
  const marketsByKey = computed(() => new Map((snapshot.value?.markets || []).map(m => [m.key, m])))
  function badgeState(fixture, player, market) {
    if (Number(fixture) !== fixtureId.value || !snapshot.value) return null
    const key = `${fixture}:${player}:${market}`
    if (snapshot.value.marketFeedModes?.[key]?.enabled === false) return null
    const enabled = snapshot.value.fixturePricingModes?.[fixture]?.enabled === true
    if (!enabled) return null
    const observed = marketsByKey.value.get(key)
    const eligible = !stale.value && observed?.decision === 'ready' && !observed.sourceMissing && !observed.stale
    const ready = eligible && !observed.existingSuspension
    const singleSource = observed?.selectionMode === 'single_source'
    const singleFeed = eligible && observed?.singleFeed === true
    const available = singleSource || eligible || (observed?.availableBookIds?.length || 0) > 0
    return { eligible, ready, available, singleSource, singleFeed, selectedBookName: observed?.selectedBookName,
      reason: stale.value ? 'engine unavailable' : !eligible ? observed?.reason?.replaceAll('_', ' ') || 'no current coverage'
        : observed?.existingSuspension ? 'player or market suspended' : singleSource ? 'selected single source'
          : singleFeed ? `single feed: ${observed.singleFeedBookName || observed.selectedBookName} (no consensus)` : 'consensus available' }
  }
  function automaticMarketSuspension(fixture, player, market, line = {}) {
    const state = badgeState(fixture, player, market)
    if (!state) return null
    if (!state.eligible) return true
    const display = snapshot.value?.pricingDisplays?.[fixture]
    if (!display || clock.value - display.generatedAt > 15000) return null
    const owned = display.automaticMarketStates?.[`${player}:${market}`]
    if (!owned || owned.directLineHold) return null
    // A newer direct market action wins over an older automatic release.
    const sourceTime = line.updated_at ? Date.parse(String(line.updated_at).replace(' ', 'T').replace(/(?<!Z)(?<![+-]\d{2}:\d{2})$/, 'Z')) : 0
    if (Number.isFinite(sourceTime) && sourceTime > owned.changedAt) return null
    return false // V2 clears only its own market-level reason once healthy.
  }
  function displayLinesFor(fixture, player, market) {
    if (badgeState(fixture, player, market)?.eligible !== true) return []
    const display = snapshot.value.pricingDisplays?.[fixture]
    const pricedAt = display?.generatedAt ?? display?.deliveredAt
    if (!display || !Number.isFinite(pricedAt) || clock.value - pricedAt > 15000 || pricedAt > clock.value + 1000) return []
    const observed = marketsByKey.value.get(`${fixture}:${player}:${market}`)
    return display.lines.filter(line => String(line.player_id) === String(player) && line.market_type === market
      && (line.source_selection_version ?? 0) === (observed?.sourceSelectionVersion ?? 0)
      && (line.market_feed_version ?? 0) === (observed?.marketFeedVersion ?? 0))
  }
  const priceRows = computed(() => {
    const display = snapshot.value?.pricingDisplays?.[fixtureId.value]
    const pricedAt = display?.generatedAt ?? display?.deliveredAt
    if (!fixtureMode.value || stale.value || !Number.isFinite(pricedAt)
      || clock.value - pricedAt > 15000 || pricedAt > clock.value + 1000) return null
    return new Map(display.lines.map(line => [`${line.player_id}:${line.market_type}:${Number(line.balance_line)}`, line]))
  })
  function priceLines(sourceLines = [], sourceFixtureId) {
    if (!priceRows.value) return sourceLines
    return sourceLines.map(line => {
      const fixture = Number(line.fixture_id ?? sourceFixtureId)
      if (fixture !== fixtureId.value) return line
      if (snapshot.value.marketFeedModes?.[`${fixture}:${line.player_id}:${line.market_type}`]?.enabled === false) return line
      const observed = marketsByKey.value.get(`${fixture}:${line.player_id}:${line.market_type}`)
      if (observed?.decision !== 'ready' || observed.stale || observed.sourceMissing) return line
      const price = priceRows.value.get(`${line.player_id}:${line.market_type}:${Number(line.balance_line)}`)
      if (price && (price.source_selection_version ?? 0) !== (observed.sourceSelectionVersion ?? 0)) return line
      if (price && (price.market_feed_version ?? 0) !== (observed.marketFeedVersion ?? 0)) return line
      // Keep the fixed source rows, their order, ids, suspension and edit metadata.
      // Only the probability/odds model changes; this does not add UI rows.
      return { ...line, balance_line_over_odds: price?.balance_line_over_odds ?? null,
        balance_line_under_odds: price?.balance_line_under_odds ?? null,
        balance_line_over_probability: price?.balance_line_over_probability ?? null,
        balance_line_under_probability: price?.balance_line_under_probability ?? null,
        is_balanced: price?.is_balanced ?? false }
    })
  }
  function stop() {
    generation++; abort?.abort(); stream?.close(); clearInterval(ticker); clearInterval(renewal); clearInterval(routingTimer)
    connection.value = 'offline'; busy.value = false
  }
  async function start(id, preserve = false) {
    stop()
    const current = generation
    if (!preserve || fixtureId.value !== Number(id)) { snapshot.value = null; routing.value = null }
    fixtureId.value = Number(id)
    loadRouting(id)
    routingTimer = setInterval(() => loadRouting(id), 60000) // publishing changes the routing
    error.value = ''; connection.value = 'connecting'; clock.value = Date.now()
    abort = new AbortController()
    const signal = abort.signal
    ticker = setInterval(() => { clock.value = Date.now() }, 1000)
    try {
      const state = await fetchFeedsV2Market({ fixtureId: fixtureId.value }, signal)
      if (current !== generation || signal.aborted) return
      snapshot.value = state
      stream = streamFeedsV2Market({ fixtureId: fixtureId.value })
      stream.addEventListener('snapshot', event => {
        if (current !== generation) return
        try { snapshot.value = JSON.parse(event.data); connection.value = 'live'; error.value = '' }
        catch { connection.value = 'reconnecting' }
      })
      stream.onerror = () => { if (current === generation) connection.value = 'reconnecting' }
      renewal = setInterval(async () => {
        try { await ensureFeedsV2Session(true); if (current === generation) start(id, true) }
        catch (cause) { if (current === generation) { error.value = cause.message; connection.value = 'offline'; stream?.close() } }
      }, 10 * 60000)
    } catch (cause) {
      if (current === generation && !signal.aborted) { error.value = cause.message; connection.value = 'offline' }
    }
  }
  async function saveEventMapping(eventIds) {
    if (busy.value || !canEdit.value) return false
    const current = generation, signal = abort.signal
    busy.value = true; error.value = ''
    try {
      const result = await setFeedsV2EventMapping(fixtureId.value, eventIds, signal)
      if (current === generation) snapshot.value = result.snapshot
      return current === generation
    } catch (cause) { if (current === generation && !signal.aborted) error.value = cause.message; return false }
    finally { if (current === generation) busy.value = false }
  }
  async function setFixtureMode(enabled) {
    if (busy.value || !canEdit.value || (enabled && !canEnable.value)) return
    const current = generation, signal = abort.signal
    busy.value = true; error.value = ''
    try {
      const result = await setFeedsV2FixturePricing(fixtureId.value, enabled, signal)
      if (current === generation) snapshot.value = result.snapshot
    } catch (cause) { if (current === generation && !signal.aborted) error.value = cause.message }
    finally { if (current === generation) busy.value = false }
  }
  return { routing, routingLabel, fixtureId, snapshot, error, busy, connection, stale, fixtureMode, canEdit, canEnable, eventMapping, requireEventMapping, saveEventMapping, eligibleCount, badgeState, automaticMarketSuspension, displayLinesFor, priceLines, start, stop, setFixtureMode }
})
